'use client';

import React, { useState } from 'react';
import StepIndicator from './StepIndicator';
import Step1SelectService from './Step1SelectService';
import Step2BranchSlot from './Step2BranchSlot';
import Step3PatientDetails from './Step3PatientDetails';
import Step4Summary from './Step4Summary';

export type SelectedService = {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  type: 'test' | 'package';
  tests?: number;
};

export type BookingData = {
  service: SelectedService | null;
  branch: string;
  branchName: string;
  date: string;
  timeSlot: string;
  homeCollection: boolean;
  homeAddress: string;
  patientName: string;
  patientAge: string;
  patientGender: string;
  patientPhone: string;
  patientEmail: string;
  prescriptionFile: string;
  corporateCode: string;
  paymentMethod: string;
};

const initialBookingData: BookingData = {
  service: null,
  branch: '',
  branchName: '',
  date: '',
  timeSlot: '',
  homeCollection: false,
  homeAddress: '',
  patientName: '',
  patientAge: '',
  patientGender: '',
  patientPhone: '',
  patientEmail: '',
  prescriptionFile: '',
  corporateCode: '',
  paymentMethod: 'online',
};

const steps = [
  { id: 1, label: 'Select Service', desc: 'Test or Package' },
  { id: 2, label: 'Branch & Slot', desc: 'Location & Time' },
  { id: 3, label: 'Patient Details', desc: 'Your Information' },
  { id: 4, label: 'Confirm & Pay', desc: 'Review & Payment' },
];

export default function BookingWizard() {
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState<BookingData>(initialBookingData);

  const updateBooking = (updates: Partial<BookingData>) => {
    setBookingData((prev) => ({ ...prev, ...updates }));
  };

  const nextStep = () => setCurrentStep((s) => Math.min(s + 1, 4));
  const prevStep = () => setCurrentStep((s) => Math.max(s - 1, 1));
  const goToStep = (step: number) => {
    if (step < currentStep) setCurrentStep(step);
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 items-start">
      {/* Main wizard */}
      <div className="xl:col-span-3 space-y-6">
        <StepIndicator steps={steps} currentStep={currentStep} onStepClick={goToStep} />

        <div className="animate-fade-in">
          {currentStep === 1 && (
            <Step1SelectService
              bookingData={bookingData}
              updateBooking={updateBooking}
              onNext={nextStep}
            />
          )}
          {currentStep === 2 && (
            <Step2BranchSlot
              bookingData={bookingData}
              updateBooking={updateBooking}
              onNext={nextStep}
              onBack={prevStep}
            />
          )}
          {currentStep === 3 && (
            <Step3PatientDetails
              bookingData={bookingData}
              updateBooking={updateBooking}
              onNext={nextStep}
              onBack={prevStep}
            />
          )}
          {currentStep === 4 && (
            <Step4Summary
              bookingData={bookingData}
              onBack={prevStep}
            />
          )}
        </div>
      </div>

      {/* Summary sidebar */}
      <div className="xl:col-span-1">
        <BookingSidebar bookingData={bookingData} currentStep={currentStep} />
      </div>
    </div>
  );
}

function BookingSidebar({ bookingData, currentStep }: { bookingData: BookingData; currentStep: number }) {
  const subtotal = bookingData.service?.price ?? 0;
  const homeCharge = bookingData.homeCollection ? 99 : 0;
  const total = subtotal + homeCharge;

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden sticky top-24">
      <div className="bg-primary px-5 py-4">
        <h3 className="text-white font-bold text-base">Booking Summary</h3>
        <p className="text-white/70 text-xs mt-0.5">Step {currentStep} of 4</p>
      </div>
      <div className="p-5 space-y-4">
        {/* Service */}
        <div>
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1.5">Selected Service</p>
          {bookingData.service ? (
            <div className="bg-secondary rounded-xl p-3">
              <p className="text-sm font-semibold text-foreground">{bookingData.service.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {bookingData.service.type === 'package' ? `${bookingData.service.tests} tests included` : 'Diagnostic Test'}
              </p>
            </div>
          ) : (
            <div className="bg-muted rounded-xl p-3 text-xs text-muted-foreground">
              No service selected yet
            </div>
          )}
        </div>

        {/* Branch */}
        {bookingData.branchName && (
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1.5">Branch</p>
            <p className="text-sm font-medium text-foreground">{bookingData.branchName}</p>
          </div>
        )}

        {/* Date & Time */}
        {bookingData.date && bookingData.timeSlot && (
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1.5">Date & Time</p>
            <p className="text-sm font-medium text-foreground">{bookingData.date}</p>
            <p className="text-xs text-muted-foreground">{bookingData.timeSlot}</p>
          </div>
        )}

        {/* Home collection */}
        {bookingData.homeCollection && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
            <span className="text-green-700 text-xs font-semibold">🏠 Home Collection Added</span>
          </div>
        )}

        {/* Patient */}
        {bookingData.patientName && (
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1.5">Patient</p>
            <p className="text-sm font-medium text-foreground">{bookingData.patientName}</p>
            {bookingData.patientAge && (
              <p className="text-xs text-muted-foreground">Age: {bookingData.patientAge} · {bookingData.patientGender}</p>
            )}
          </div>
        )}

        {/* Pricing */}
        {subtotal > 0 && (
          <div className="border-t border-border pt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Service</span>
              <span className="font-medium text-foreground">₹{subtotal.toLocaleString('en-IN')}</span>
            </div>
            {bookingData.homeCollection && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Home Collection</span>
                <span className="font-medium text-foreground">₹{homeCharge}</span>
              </div>
            )}
            {bookingData.service?.originalPrice && bookingData.service.originalPrice > subtotal && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-green-600 font-medium">You Save</span>
                <span className="text-green-600 font-bold">
                  ₹{(bookingData.service.originalPrice - subtotal).toLocaleString('en-IN')}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between font-bold text-base pt-2 border-t border-border">
              <span className="text-foreground">Total</span>
              <span className="text-primary stats-number">₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
        )}

        {/* NABL badge */}
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
          <span className="badge-nabl text-xs">NABL</span>
          <span className="text-green-700 text-xs font-medium">Accredited Lab</span>
        </div>
      </div>
    </div>
  );
}